import { forwardRef, useImperativeHandle, useState } from 'react'
import { useRouter } from 'next/router'

import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast'

import PanelKinerja from 'app/KinerjaPage/components/PanelKinerja'
import {
  TableBody,
  TableHeader,
  TableHeaderItem,
  TablePelaksanaanKinerja,
  TableRow,
  TableRowItem,
} from 'app/KinerjaPage/components/TablePelaksanaanKinerja'
import DialogFinalizeSKP from './components/DialogFinalizeSKP'
import TabRangkumanWaitingApproval from './WaitingApproval'
import useContentContext from 'app/KinerjaPage/hooks/useContentContext'
import {
  useAPIKinerjaProfile,
  useAPIUpdateState,
} from 'api/kinerja/useAPIKinerja'
import StickyFooter from 'app/KinerjaPage/components/StickyFooter'
import useSKPTrackEvent from 'app/KinerjaPage/hooks/useSKPTrackEvent'
import { PENGELOLAAN_KINERJA_GURU_PATH } from 'app/KinerjaShared/constants/path'

import type { ForwardedRef } from 'react'
import type {
  KinerjaPerencanaanTabBaseProps,
  PerilakuKerjaPayload,
  SKPTabHandler,
} from 'types/kinerja'

interface PerencanaanTabRangkumanProps extends KinerjaPerencanaanTabBaseProps {
  onRequestChange?: () => void
  onClickBackToHome?: () => void
}

const TabRangkuman = (
  {
    skp,
    isSubmitting,
    onRequestChange = () => {},
    onClickBackToHome = () => {},
  }: PerencanaanTabRangkumanProps,
  ref: ForwardedRef<SKPTabHandler>
) => {
  const {
    getKinerjaUtamaById,
    getPengembanganKompetensiById,
    getTugasTambahanById,
    getPraktikPembelajaranById,
    getAspekPerilakuKerjaById,
  } = useContentContext()
  const router = useRouter()
  const toast = useToast()
  const { trackEvent } = useSKPTrackEvent()

  const getPerilakuKerjaRow = (value: PerilakuKerjaPayload) => {
    const aspek = getAspekPerilakuKerjaById(value.aspekId)
    const indikator = aspek?.indikator.find(
      ({ id }) => id === value.indikatorId
    )

    const ekspektasi = indikator?.ekspektasi.find(
      ({ id }) => id === value.ekspektasiId
    )

    return {
      aspek: aspek?.value ?? '',
      indikator: indikator?.value ?? '',
      ekspektasi: ekspektasi?.value ?? '',
    }
  }

  const selectedPraktikPembelajaran = getPraktikPembelajaranById(
    skp.praktikPembelajaran
  )

  const filteredTugasTambahan = skp.tugasTambahan.filter(
    (value) => value.id !== 0
  )

  const [isOpenDialogFinalize, setIsOpenDialogFinalize] = useState(false)

  const { data: profile } = useAPIKinerjaProfile()
  const {
    mutateAsync: finalizeSKP,
    isLoading: isFinalizingSKP,
    isSuccess: isSuccessFinalize,
  } = useAPIUpdateState()

  const handleClickFinalize = async () => {
    trackEvent({
      event_name: 'CLICK_KINERJA_PROPOSE_PLANNING',
      screen_name: 'KINERJA_PLANNING_PENGAJUAN_POP_UP',
      kinerja_planning_id: skp?.id,
      kinerja_planning_status: skp?.status,
    })

    try {
      await finalizeSKP({
        skpId: skp.id,
      })

      setIsOpenDialogFinalize(false)
    } catch (e) {
      console.error(e)

      toast({
        message: 'Gagal mengajukan SKP',
      })
    }
  }

  useImperativeHandle(
    ref,
    () => ({
      saveDraft() {
        router.push(`${PENGELOLAAN_KINERJA_GURU_PATH}`)
      },
    }),
    [router]
  )

  if (isSuccessFinalize) {
    return <TabRangkumanWaitingApproval onClickBackToHome={onClickBackToHome} />
  }

  return (
    <div>
      <div className="py-9 px-6">
        <div className="flex flex-col gap-1">
          <Text variant="heading-md">Rangkuman Perencanaan Kinerja Anda</Text>
          <Text color="subdued">
            Anda bisa cek ulang dan ubah sebelum mengajukan ke atasan. Format
            Perencanaan Kinerja akan kami sesuaikan dengan aturan yang berlaku.
          </Text>
        </div>

        <section className="mt-9">
          <Text as="h2" variant="heading-sm" className="mb-4">
            Hasil Kerja Utama
          </Text>

          <div className="flex flex-col gap-6">
            <PanelKinerja
              key={`PanelKinerjaUtama-PraktikPembelajaran`}
              data-testid={`PanelKinerjaUtama-PraktikPembelajaran`}
              number="1"
              kinerja={selectedPraktikPembelajaran}
              additionalInfo="Guru akan mengisi Formulir Diskusi Persiapan, Formulir Diskusi Tindak Lanjut, dan Formulir Refleksi Tindak Lanjut."
            />

            {skp.pengembanganKompetensi.map((kinerja, idx) => {
              const selectedPengembanganKompetensi =
                getPengembanganKompetensiById(kinerja.id)

              return (
                <PanelKinerja
                  key={`PanelKinerja-PengembanganKompetensi-${idx}`}
                  data-testid={`PanelKinerja-PengembanganKompetensi-${idx}`}
                  number={idx + 2}
                  kinerja={{
                    ...selectedPengembanganKompetensi,
                    targetKuantitas: `${kinerja.kuantitas} kegiatan`,
                    bobot:
                      selectedPengembanganKompetensi.bobot * kinerja.kuantitas,
                  }}
                />
              )
            })}

            {skp.kinerjaUtama.map((kinerja, idx) => {
              const kinerjaUtama = getKinerjaUtamaById(kinerja)

              return (
                <PanelKinerja
                  key={`PanelKinerjaUtama-${kinerja}`}
                  data-testid={`PanelKinerjaUtama-${kinerja}`}
                  kinerja={kinerjaUtama}
                  number={idx + skp.pengembanganKompetensi.length + 2}
                />
              )
            })}
          </div>
        </section>

        {filteredTugasTambahan.length > 0 && (
          <section className="mt-9">
            <Text as="h2" variant="heading-sm" className="mb-4">
              Hasil Kerja Tambahan
            </Text>

            <div className="flex flex-col gap-6">
              {filteredTugasTambahan.map((kinerja, idx) => {
                const selectedTugasTambahan = getTugasTambahanById(kinerja.id)

                return (
                  <PanelKinerja
                    key={`PanelKinerjaTambahan-${idx}`}
                    data-testid={`PanelKinerjaTambahan-${idx}`}
                    number={idx + 1}
                    kinerja={selectedTugasTambahan}
                  />
                )
              })}
            </div>
          </section>
        )}

        <section className="mt-9">
          <Text as="h2" variant="heading-sm">
            Perilaku Kerja
          </Text>

          <TablePelaksanaanKinerja>
            <TableHeader>
              <TableHeaderItem>No.</TableHeaderItem>
              <TableHeaderItem>Aspek</TableHeaderItem>
              <TableHeaderItem>Indikator</TableHeaderItem>
              <TableHeaderItem>Perwujudan</TableHeaderItem>
            </TableHeader>
            <TableBody>
              {skp.prilakuKerja
                .map(getPerilakuKerjaRow)
                .map((perilakuKerja, idx) => (
                  <TableRow key={`perilaku-kerja-${idx}`}>
                    <TableRowItem>{idx + 1}</TableRowItem>
                    <TableRowItem>{perilakuKerja.aspek}</TableRowItem>
                    <TableRowItem>{perilakuKerja.indikator}</TableRowItem>
                    <TableRowItem>{perilakuKerja.ekspektasi}</TableRowItem>
                  </TableRow>
                ))}
            </TableBody>
          </TablePelaksanaanKinerja>
        </section>
      </div>

      <StickyFooter>
        <Button size="lg" color="white" onClick={onRequestChange}>
          Ubah
        </Button>
        <Button
          size="lg"
          isLoading={isSubmitting}
          disabled={isSubmitting}
          type="submit"
          data-testid="btn-next"
          onClick={() => {
            trackEvent({
              event_name: 'CLICK_KINERJA_PLANNING_TO_POP_UP_CONFIRMATION',
              screen_name: 'KINERJA_PLANNING_PERILAKU_KERJA_PAGE',
              kinerja_planning_id: skp?.id,
              kinerja_planning_status: skp?.status,
            })
            setIsOpenDialogFinalize(true)
          }}
        >
          Lanjut ajukan
        </Button>

        <DialogFinalizeSKP
          data-testid="dialog-finalize-skp"
          isOpen={isOpenDialogFinalize}
          onClose={() => setIsOpenDialogFinalize(false)}
          isFinalizing={isFinalizingSKP}
          onClickCancel={() => {
            setIsOpenDialogFinalize(false)
            router.push(PENGELOLAAN_KINERJA_GURU_PATH)
          }}
          onClickFinalize={handleClickFinalize}
          satuanPendidikan={profile?.data.satuanPendidikan}
        />
      </StickyFooter>
    </div>
  )
}

export default forwardRef<SKPTabHandler, PerencanaanTabRangkumanProps>(
  TabRangkuman
)
